import { useState, useEffect, useMemo } from "react";
import { ArrowUpRight, Search, Filter, Clock, Info, ChevronDown } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_STYLES = {
  paid: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
  open: "bg-blue-500/15 text-blue-400 border-blue-500/20",
  partial: "bg-amber-500/15 text-amber-400 border-amber-500/20",
  overdue: "bg-red-500/15 text-red-400 border-red-500/20",
};

export default function AccountsReceivable() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("days_overdue");
  const [sortDir, setSortDir] = useState("desc");

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(`${API}/quickbooks/invoices?sort_by=${sortBy}&sort_dir=${sortDir}`, { headers });
        if (!res.ok) return;
        setData(await res.json());
      } catch {
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [sortBy, sortDir]);

  const filtered = useMemo(() => {
    if (!data?.invoices) return [];
    return data.invoices.filter(inv => {
      if (statusFilter !== "all" && inv.status !== statusFilter) return false;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return (
          inv.doc_number?.toLowerCase().includes(term) ||
          inv.customer_name?.toLowerCase().includes(term)
        );
      }
      return true;
    });
  }, [data, searchTerm, statusFilter]);

  // Aging buckets from filtered open invoices
  const aging = useMemo(() => {
    const buckets = { current: 0, "1_15": 0, "16_30": 0, "31_60": 0, "61_90": 0, "90_plus": 0 };
    filtered.forEach(inv => {
      if (inv.balance <= 0) return;
      const d = inv.days_overdue;
      if (d <= 0) buckets.current += inv.balance;
      else if (d <= 15) buckets["1_15"] += inv.balance;
      else if (d <= 30) buckets["16_30"] += inv.balance;
      else if (d <= 60) buckets["31_60"] += inv.balance;
      else if (d <= 90) buckets["61_90"] += inv.balance;
      else buckets["90_plus"] += inv.balance;
    });
    return buckets;
  }, [filtered]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="w-6 h-6 border-2 border-white/20 border-t-white/70 rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center py-32">
        <Clock className="w-10 h-10 text-white/20 mb-4" />
        <p className="text-white/40 text-sm">No data yet. Run a sync first.</p>
      </div>
    );
  }

  const kpis = data.kpis;

  return (
    <div className="space-y-6" data-testid="ar-tab">
      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Total Open A/R", value: kpis.total_open_ar, color: "text-white" },
          { label: "Current A/R", value: kpis.current_ar, color: "text-blue-400" },
          { label: "Overdue A/R", value: kpis.overdue_ar, color: "text-red-400" },
          { label: "Total Invoices", value: data.count, format: "number" },
        ].map((item, i) => (
          <div key={i} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
            <p className="text-[11px] text-white/40 uppercase tracking-wider mb-1">{item.label}</p>
            <p className={`text-2xl font-light tracking-tight ${item.color || "text-white/80"}`}>
              {item.format === "number"
                ? item.value.toLocaleString()
                : `$${item.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
            </p>
          </div>
        ))}
      </div>

      {/* Aging Summary */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
        <h3 className="text-sm font-medium text-white/60 mb-3 uppercase tracking-wider">Aging Summary</h3>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {[
            { key: "current", label: "Current", color: "border-emerald-500/40" },
            { key: "1_15", label: "1-15 Days", color: "border-yellow-500/40" },
            { key: "16_30", label: "16-30 Days", color: "border-amber-500/40" },
            { key: "31_60", label: "31-60 Days", color: "border-orange-500/40" },
            { key: "61_90", label: "61-90 Days", color: "border-red-500/40" },
            { key: "90_plus", label: "90+ Days", color: "border-red-700/40" },
          ].map(bucket => (
            <div key={bucket.key} className={`border-l-2 ${bucket.color} pl-3 py-1`}>
              <p className="text-[10px] text-white/40 mb-0.5">{bucket.label}</p>
              <p className="text-sm font-semibold text-white/80">
                ${(aging[bucket.key] || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input
            type="text"
            placeholder="Search invoices..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="ar-search"
            className="w-full pl-9 pr-4 py-2 bg-white/[0.03] border border-white/[0.08] rounded-lg text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-white/20"
          />
        </div>
        <div className="flex gap-1.5">
          {["all", "open", "partial", "overdue", "paid"].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              data-testid={`filter-${s}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all capitalize ${
                statusFilter === s
                  ? "bg-white/10 text-white border border-white/20"
                  : "text-white/40 border border-white/[0.06] hover:bg-white/[0.04]"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Invoice Table */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ar-table">
            <thead>
              <tr className="text-white/30 text-[11px] uppercase tracking-wider bg-white/[0.02]">
                <th className="text-left px-4 py-3 font-medium">Invoice #</th>
                <th className="text-left px-4 py-3 font-medium">Client</th>
                <th className="text-left px-4 py-3 font-medium">Invoice Date</th>
                <th className="text-left px-4 py-3 font-medium">Due Date</th>
                <th className="text-right px-4 py-3 font-medium">Amount</th>
                <th className="text-right px-4 py-3 font-medium">Balance</th>
                <th className="text-center px-4 py-3 font-medium">Days Overdue</th>
                <th className="text-center px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.04]">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-white/30 text-sm">
                    No invoices match your filters
                  </td>
                </tr>
              ) : (
                filtered.map((inv, i) => (
                  <tr key={i} className="hover:bg-white/[0.02] transition-colors">
                    <td className="px-4 py-3 text-white/80 font-medium">{inv.doc_number}</td>
                    <td className="px-4 py-3 text-white/60">{inv.customer_name}</td>
                    <td className="px-4 py-3 text-white/40 text-xs">{inv.txn_date}</td>
                    <td className="px-4 py-3 text-white/40 text-xs">{inv.due_date}</td>
                    <td className="px-4 py-3 text-right text-white/60">
                      ${inv.total_amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </td>
                    <td className="px-4 py-3 text-right text-white/80 font-medium">
                      ${inv.balance.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {inv.days_overdue > 0 ? (
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          inv.days_overdue > 60 ? "bg-red-500/20 text-red-400"
                          : inv.days_overdue > 30 ? "bg-orange-500/20 text-orange-400"
                          : "bg-amber-500/20 text-amber-400"
                        }`}>
                          {inv.days_overdue}d
                        </span>
                      ) : (
                        <span className="text-white/20 text-xs">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium border capitalize ${STATUS_STYLES[inv.status] || ""}`}>
                        {inv.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
